import { put, takeEvery, all, call, select, delay } from 'redux-saga/effects';
import { setAction } from '../../LayoutEditorPage/actions/layoutEditor';
import { loadGoogleSheetAction, loadLiveDataAction } from '../actions/chartEditor';
import actionTypes from '../../../redux/actions/action-types';
import { getProjectConfig } from '../../../redux/selectors/projectConfig';
import DefaultData from '../meta/data/DefaultData';
import { setAction as setProjectConfigAction } from '../../../redux/actions/projectConfig';
import { updateAggregated, initChart } from './ChartEditor';
import { merge } from 'highed';
import {
  checkSections,
  getAllMergedLabelAndData,
  getCSVFromText,
  parseCSV,
  getLetterFromIndex,
  toCSV,
  clean,
  getLetterIndex,
  toMapCSV
} from '../../../editor/core/highcharts-editor';
import DataImporterHelper from '../../../editor/core-ui/highed.data.importer.helper';
import { cloneDeep, isArray, isObject } from 'lodash';

import { convertHTMLEntities } from '../../../editor/ui/chartpreview/highed.chartpreview.utils';
import templates from '../../../editor/core/highed.template.plugins';
import {
  isShortCode,
  isLongCode,
  getRandomMapData,
  isPointMap,
  isPatternFill,
  isPointMapFromConfig
} from './ChartEditorMapHelper';
import { getChartConfig } from '../selectors/chartEditor';
import assignDefaults from './../../../editor/meta/highed.meta.assign.defaults';
import chartTypes from './../../../editor/meta/highed.meta.charttype';

let dataImporterHelper = new DataImporterHelper();
const defaultMap = 'countries/us/us-all';

/**
 * Compile data when initially loading a project
 * @param {*} projectData - Project config options
 * @returns csv, columnLength
 */
export function loadData(projectData, customizedOptions) {
  let columnLength = false;
  let dataProvider = projectData.settings.dataProvider;
  let csv;
  let chartType = 'chart';

  if (dataProvider.seriesMapping) {
    merge(customizedOptions, {
      data: {
        seriesMapping: dataProvider.seriesMapping
      }
    });
  }

  if (dataProvider.googleSpreadsheet) {
    customizedOptions.data = projectData.settings.dataProvider.googleSpreadsheet;
  } else if (dataProvider.liveData) {
    customizedOptions.data = projectData.settings.dataProvider.liveData;
  } else if (dataProvider.csv) {
    // We need to fix potential html-entities as they will mess up separators
    dataProvider.csv = convertHTMLEntities(dataProvider.csv);

    let sections = dataProvider.assignDataFields;
    csv = dataProvider.csv.replace('&amp;', '&');

    if (sections) {
      projectData.options.data = cloneDeep(dataProvider);
      sections = getAllMergedLabelAndData(sections);
      let parsedCSV;

      if (projectData.options && (projectData.options.chart?.map || projectData.options.plotOptions?.map)) {
        chartType = 'Map';
        csv = parseCSV(csv, null, sections)
          .map(function (cols) {
            return cols.join(';');
          })
          .join('\n');
        parsedCSV = csv;
      } else {
        parsedCSV = parseCSV(csv, null, sections);
        parsedCSV[0] = parsedCSV[0].map((csv) => (csv === null ? 'null' : csv));

        if (projectData?.settings?.template[0]?.templateParseData) {
          const parseData = templates[projectData.settings.template[0].templateParseData].parseData;
          parsedCSV = parseData(parseCSV(dataProvider.csv), null, projectData.options);
        }

        // We're not using parsedCSV result for this as that relies on the assigned data section.
        // User could have non-assigned values that need to be accounted for.
        const headers = csv.split('\n')[0];
        if (headers) columnLength = headers.split(';').length;

        parsedCSV = toCSV(parsedCSV);
      }

      projectData.options.data.csv = parsedCSV;
      customizedOptions.data = projectData.options.data;
    } else {
      projectData.options.data = dataProvider;
      customizedOptions.data = projectData.options.data;
    }

    // if ( customizedOptions.data &&  customizedOptions.data.csv) M.lastLoadedCSV = M.customizedOptions.data.csv;
  }

  return { csv, columnLength, chartType };
}

export function* deleteSeries(params) {
  let { length } = params.data;
  const config = yield select(getProjectConfig);
  const customizedOptions = merge({}, config.customizedOptions);

  if (customizedOptions && customizedOptions.series) {
    customizedOptions.series = customizedOptions.series.slice(0, length);
    yield put(
      setProjectConfigAction({
        customizedOptions
      })
    );
    yield call(updateAggregated);
    yield call(initChart);
  }
}

export function* deleteSingleSeries(params) {
  let { index } = params.data;
  const config = yield select(getProjectConfig);
  const customizedOptions = merge({}, config.customizedOptions);

  if (customizedOptions.series && customizedOptions.series[index]) {
    customizedOptions.series.splice(index, 1);
    // chartTemplates.deleteTemplateOption(index);
  }

  yield put(
    setProjectConfigAction({
      customizedOptions
    })
  );

  yield call(updateAggregated);
  yield call(initChart);
}

function generateTypeObject(typeObject) {
  let newOptions = cloneDeep(assignDefaults);
  newOptions = clean(merge(newOptions, typeObject));

  clean(newOptions);
  return Object.keys(newOptions)
    .sort((a, b) => (newOptions[a].order ?? 0) - (newOptions[b].order ?? 0))
    .reduce(function (result, key) {
      result[key] = newOptions[key];
      return result;
    }, {});
}

export function* addBlankSeries(params) {
  try {
    let { index, type, extra, skipEmit, customizedOptions, seriesAssigns } = params.data;

    if (!customizedOptions) {
      const config = yield select(getProjectConfig);
      customizedOptions = merge({}, config.customizedOptions);
    }

    if (!extra) {
      extra = {};
    }

    if (!customizedOptions.series) {
      customizedOptions.series = [];
    }

    if (!customizedOptions.series[index]) {
      customizedOptions.series[index] = merge(
        {
          data: [],
          turboThreshold: 0,
          compare: undefined
        },
        extra
      );
    }

    if (type) customizedOptions.series[index].type = type;

    if (extra) {
      customizedOptions.series[index] = merge(customizedOptions.series[index], extra);
    }

    let updatedProjectOptions = {};
    if (seriesAssigns) {
      let newOptions = generateTypeObject(chartTypes[type]);

      if (seriesAssigns.length) {
        // Use last options, value = index * dataFields
        const lastOption = seriesAssigns[seriesAssigns.length - 1];
        let options = {};
        Object.keys(lastOption).forEach((key) => {
          if (lastOption[key].isData) options[key] = lastOption[key];
        });
        const dataKeys = Object.keys(options);
        dataKeys.forEach((key) => {
          const dataOption = options[key];
          if (newOptions[key]) {
            newOptions[key].rawValue = [dataOption.rawValue[0] + dataKeys.length];
            newOptions[key].value = getLetterFromIndex(dataOption.rawValue[0] + dataKeys.length);
          }
        });
      }
      seriesAssigns.push(merge({}, newOptions));
      updatedProjectOptions.seriesAssigns = seriesAssigns;
    }

    // Init the initial chart
    if (skipEmit) return customizedOptions;

    yield put(setProjectConfigAction({ customizedOptions, ...updatedProjectOptions }));
    yield call(updateAggregated);
    yield call(initChart);
  } catch (error) {
    throw new Error(error);
  }
}

export function* setTemplateData(customizedOptions, chosenWizardTemplate, isNowTilemap, hcCode) {
  if (!chosenWizardTemplate) {
    const chartConfig = yield select(getChartConfig);
    chosenWizardTemplate = chartConfig.chosenWizardTemplate;
  }

  const isNowPointMap = isPointMap(chosenWizardTemplate);
  const isNowPatternFill = isPatternFill(chosenWizardTemplate);
  // Wizard stuff
  const type = chosenWizardTemplate.defaultDataType ?? chosenWizardTemplate.config?.chart?.type;

  const defaultOptions = DefaultData[type] ?? DefaultData.line;
  let csv = false;
  let isMap = customizedOptions?.chart?.map;

  if (chosenWizardTemplate.parseData) {
    // Needs some special parsing of data
    const templatePlugin = templates[chosenWizardTemplate.parseData].parseData;
    csv = toCSV(templatePlugin(parseCSV(defaultOptions.data), null, customizedOptions));
  }

  if (isMap && customizedOptions?.chart?.map !== defaultMap && !isNowTilemap && !isNowPointMap && !isNowPatternFill) {
    csv = getRandomMapData(Highcharts.maps[customizedOptions.chart.map].features, hcCode);
    customizedOptions.data.csv = csv;
  }

  customizedOptions.data = {
    csv: csv || defaultOptions.data
  };

  customizedOptions.data.seriesMapping = defaultOptions.seriesMapping ?? [];
  customizedOptions.series = [];

  // let assignDataFields = defaultOptions.assignDataFields ?? [];
  let seriesAssigns = [];

  for (let i = 0; i < defaultOptions.series; i++) {
    customizedOptions = yield call(addBlankSeries, {
      data: {
        customizedOptions,
        index: i,
        skipEmit: true,
        extra: defaultOptions.seriesOptions ? defaultOptions.seriesOptions[i] : null,
        seriesAssigns,
        type: defaultOptions.seriesTemplate?.[i] ?? chosenWizardTemplate?.config?.chart?.type
      }
    });

    if (!defaultOptions.seriesMapping) {
      customizedOptions.data.seriesMapping.push({
        x: 0,
        y: i + 1
      });
    }
  }

  if (defaultOptions.assignDataFields) {
    if (defaultOptions.assignDataFields.length !== seriesAssigns.length) {
      // Quick fix for packed bubble type
      seriesAssigns = [{ ...seriesAssigns[0] }];
    } else {
      seriesAssigns.forEach((series, seriesIndex) => {
        const assign = defaultOptions.assignDataFields[seriesIndex];
        Object.keys(assign).forEach((key) => {
          if (series[key]) {
            series[key].value = assign[key];
            series[key].rawValue = [getLetterIndex(assign[key])];
          }
        });
      });
    }
  }

  yield put(
    setProjectConfigAction({
      seriesAssigns
    })
  );

  return {
    customizedOptions,
    defaultOptions
  };
}

export function* clearData(params) {
  let { skipReinit } = params.data;

  const config = yield select(getProjectConfig);
  const customizedOptions = merge({}, config.customizedOptions);
  /*

  M.lastLoadedCSV = false;
  M.lastLoadedSheet = false;
  M.lastLoadedLiveData = false;
*/
  if (customizedOptions && customizedOptions.data) {
    customizedOptions.data = {};
  }

  if (customizedOptions.series) {
    customizedOptions.series = isArray(customizedOptions.series)
      ? customizedOptions.series
      : [customizedOptions.series];

    customizedOptions.series.forEach(function (series) {
      if (series.data) {
        delete series.data;
      }
    });
  }

  yield put(
    setProjectConfigAction({
      customizedOptions
    })
  );

  if (!skipReinit) {
    yield call(updateAggregated);
    yield call(initChart);
    /*
    updateAggregated();
    init(M.aggregatedOptions);
    emitChange();*/
  }
}

export function* loadLiveData(params) {
  let { options } = params.data;

  const config = yield select(getProjectConfig);
  const customizedOptions = merge({}, config.customizedOptions);

  /*
  M.lastLoadedLiveData = settings;

  M.lastLoadedCSV = false;
  M.lastLoadedSheet = false;
  M.lastLoadedText = false;

  merge(customizedOptions, {
    data: M.lastLoadedLiveData
  });
  */

  merge(customizedOptions, {
    data: options
  });

  if (customizedOptions && customizedOptions.data && customizedOptions.data.csv) {
    delete customizedOptions.data.csv;
  }
  if (customizedOptions && customizedOptions.data && customizedOptions.data.seriesMapping) {
    delete customizedOptions.data.seriesMapping;
  }

  if (customizedOptions && customizedOptions.series && customizedOptions.series.length > 0) {
    customizedOptions.series.forEach((serie) => {
      if ('_colorIndex' in serie) {
        delete serie._colorIndex;
      }
    });
  }

  yield put(
    setProjectConfigAction({
      customizedOptions,
      dataOptions: [[]]
    })
  );

  yield call(updateAggregated);
  yield call(initChart);

  /*
  events.emit('ProviderLiveData', settings);
  updateAggregated();
  init(M.aggregatedOptions);
*/

  // The sheet will be loaded async, so we should listen to the load event.
  /*
  gc(function(chart) {
    var found = Highcharts.addEvent(chart, 'load', function() {
      loadSeriesFromDataSource();
      found();
    });
  });*/
}

export function* loadGoogleSheet(params) {
  let { options } = params.data;

  let key;
  const config = yield select(getProjectConfig);
  const customizedOptions = merge({}, config.customizedOptions);
  /*
  M.lastLoadedCSV = false;
  M.lastLoadedSheet = options;

  M.lastLoadedSheet.googleSpreadsheetKey =
    M.lastLoadedSheet.googleSpreadsheetKey || M.lastLoadedSheet.id;*/

  if (options && (options.googleSpreadsheetKey || '').indexOf('http') === 0) {
    // Parse out the spreadsheet ID
    // Located between /d/ and the next slash after that
    key = options.googleSpreadsheetKey;
    key = key.substr(key.indexOf('/d/') + 3);
    key = key.substr(0, key.indexOf('/'));

    options.googleSpreadsheetKey = key;
  }

  merge(customizedOptions, {
    data: options // M.lastLoadedSheet
  });
  /*
  if (highed.chartType === 'Map' && customizedOptions.series) {
    addBlankSeries(customizedOptions.series.length,null,{
      joinBy: null
    });
  }
*/
  if (customizedOptions && customizedOptions.data && customizedOptions.data.csv) {
    delete customizedOptions.data.csv;
  }
  if (customizedOptions && customizedOptions.data && customizedOptions.data.seriesMapping) {
    delete customizedOptions.data.seriesMapping;
  }

  if (customizedOptions && customizedOptions.series && customizedOptions.series.length > 0) {
    customizedOptions.series.forEach((serie) => {
      if ('_colorIndex' in serie) {
        delete serie._colorIndex;
      }
    });
  }

  yield put(
    setProjectConfigAction({
      customizedOptions,
      dataOptions: [[]]
    })
  );

  yield call(updateAggregated);
  yield call(initChart);

  /*
  updateAggregated();

  init(merge({},M.aggregatedOptions));*/

  // The sheet will be loaded async, so we should listen to the load event.

  /* gc(function(chart) {
    var found = Highcharts.addEvent(chart, 'load', function() {*/
  //   loadSeriesFromDataSource();
  // found();
  //  });
  //  });
}

export function* loadMapCSV(params) {
  let { options, skipMapping } = params.data;
  let codeIndex = 0;

  const config = yield select(getProjectConfig);
  let customizedOptions = merge({}, config.customizedOptions);
  const isTilemap = customizedOptions?.chart?.type === 'tilemap';
  const isPointMap = isPointMapFromConfig(customizedOptions);

  if (skipMapping || isTilemap || isPointMap) {
    // Used for point maps and tilemap

    const dataOptions = cloneDeep(options);
    if (!skipMapping && isPointMap) {
      (options ?? []).forEach((row) => row?.splice(3, row.length - 1));
    }

    if (isTilemap || isPointMap) options = toCSV(options);

    customizedOptions.data = merge(customizedOptions.data, {
      ...customizedOptions.data,
      csv: options
    });

    yield put(
      setProjectConfigAction({
        customizedOptions,
        dataOptions
      })
    );

    yield call(updateAggregated);
    yield call(initChart);
    return;
  }

  let isCode2 = options.every((d, index) => {
      return index === 0 || d[codeIndex].length === 2;
    }),
    isCode3,
    isGeoJSON = isObject(customizedOptions.chart.map),
    mapData = isGeoJSON ? customizedOptions.chart.map.features : Highcharts.maps[customizedOptions.chart.map].features;

  if (!isCode2) {
    isCode3 = options.every((d, index) => {
      return index === 0 || d[codeIndex].length === 3;
    });
  }

  const headers = options[0];
  let codeHeader = false;
  if (headers && isArray(headers)) codeHeader = headers[0];

  let hcCode, hcName;

  if (isGeoJSON) {
    hcCode = customizedOptions.chart.map.hccode;
    hcName = customizedOptions.chart.map.hcname;
  } else {
    hcCode = 'hc-key';
    hcName = 'name';
  }

  options.forEach((d, index) => {
    if (index === 0) return;
    let code = d[codeIndex];
    // iso-a2, iso-a3, name (Africa)
    // hc-key, hc-a2, name  (US)

    mapData.forEach((mData) => {
      if (mData.properties) {
        if (
          (isCode2 && isShortCode(mData, code)) ||
          (isCode3 && isLongCode(mData, code)) ||
          (mData.properties['hc-key'] && mData.properties['hc-key'] === code) ||
          mData.properties[hcCode] === code ||
          (!isCode3 && !isCode2 && mData.properties[hcName] === code) ||
          // eslint-disable-next-line eqeqeq
          (codeHeader && mData.properties[codeHeader] && mData.properties[codeHeader] == code)
        ) {
          options[index][codeIndex] = mData.properties[hcCode];
        }
      }
    });
  });

  let newData = [];
  let length = options[0].length;

  newData.push(options[0]); // Headers
  mapData.forEach((data) => {
    if (data.properties[hcCode]) {
      let oldData = options.filter((d) => {
        return d[0] === data.properties[hcCode];
      });

      if (oldData && oldData.length > 0) {
        // Part of map collection, assign found map collection val
        if (oldData.length > 1) newData = newData.concat(oldData);
        else newData.push(...oldData);
      } else {
        // Not part of data but in map collection. Create vals for it.
        let arr = Array(length).fill(null, 0);
        arr[0] = data.properties[hcCode];
        newData.push(arr);
      }
    }
  });

  let seriesMapping = customizedOptions.data.seriesMapping;
  seriesMapping.forEach((mapping) => {
    if (mapping['hc-key']) {
      const val = mapping['hc-key'];
      delete mapping['hc-key'];
      mapping[hcCode] = val;
    }
  });

  let dataCSV;
  if (length > 2) dataCSV = toMapCSV(newData.map((data) => [data[0], data[1]]));
  else dataCSV = newData;

  customizedOptions.data = merge(customizedOptions.data, {
    ...customizedOptions.data,
    csv: dataCSV,
    seriesMapping: seriesMapping
  });

  yield put(
    setProjectConfigAction({
      customizedOptions,
      dataOptions: newData
    })
  );

  yield call(updateAggregated);
  yield call(initChart);
}

export function* loadChartCSV(params) {
  let { options } = params.data;

  const config = yield select(getProjectConfig);
  let customizedOptions = merge({}, config.customizedOptions);
  const aggregatedOptions = merge({}, config.aggregatedOptions) ?? {};
  let assignOptions = [];
  let parseAssigns = false;
  let parsedData = false;
  let rawOptions = cloneDeep(options);
  let templateParseData = config?.templateMeta[0]?.templateParseData;

  if (templateParseData) {
    const parseData = templates[templateParseData].parseData;
    parseAssigns = templates[templateParseData].parseAssigns;
    options = parseData(options, true, customizedOptions);
    parsedData = options;
  }

  const dataOptions = yield call(dataImporterHelper.parseWizardData, customizedOptions, aggregatedOptions, options);

  if (!customizedOptions.series) customizedOptions.series = [];

  if (parseAssigns) {
    // Template is a special case and doesnt fall into normal series mapping workflow
    // Use custom processing for this. This is set up on the template type.
    const seriesMapping = parseAssigns(options, dataOptions.assigns);
    customizedOptions.data = {
      csv: toCSV(options),
      seriesMapping
    };
  } else {
    if (dataOptions.assigns.length) {
      if (customizedOptions.series.length > dataOptions.assigns.length) {
        customizedOptions.series.splice(
          dataOptions.assigns.length,
          customizedOptions.series.length - dataOptions.assigns.length
        );
      } else if (customizedOptions.series.length < dataOptions.assigns.length) {
        const diff = dataOptions.assigns.length - customizedOptions.series.length;
        for (let i = 1; i <= diff; i++) {
          customizedOptions = yield call(addBlankSeries, {
            data: {
              index: (customizedOptions.series ?? []).length,
              skipEmit: true,
              customizedOptions
            }
          });
        }
      }
    }

    dataOptions.assigns.forEach((serie, i) => {
      if (!assignOptions[i]) assignOptions[i] = {};
      Object.keys(serie).forEach((key) => {
        assignOptions[i][key] = serie[key].value;
      });
    });

    const section = getAllMergedLabelAndData(assignOptions);
    let dataFieldsUsed = [];

    dataOptions.rawData[0].forEach((col, index) => {
      if (section && !checkSections(section, index)) return;
      if (dataFieldsUsed.indexOf(index) === -1) {
        dataFieldsUsed.push(index);
      }
    });

    customizedOptions = merge(customizedOptions, dataOptions.customizedOptionChanges);

    // eslint-disable-next-line no-use-before-define
    const seriesMapping = yield call(getSeriesMapping, dataOptions.assigns, dataFieldsUsed);
    if (!parsedData) {
      parsedData = parseCSV(dataOptions.csv, null, getAllMergedLabelAndData(assignOptions));
    }

    customizedOptions.data = {
      csv: toCSV(parsedData),
      seriesMapping
    };
  }

  yield put(
    setProjectConfigAction({
      customizedOptions,
      dataOptions: templateParseData ? rawOptions : dataOptions.rawData ?? rawOptions, // dataOptions.rawData
      seriesAssigns: dataOptions.assigns
    })
  );

  yield call(updateAggregated);
  yield call(initChart);
}

export function* loadCSV(params) {
  let { skipDataHelper } = params.data;

  if (skipDataHelper) yield call(loadMapCSV, params);
  else yield call(loadChartCSV, params);
}

export function* loadTextData() {
  const config = yield select(getProjectConfig);
  const chartConfig = yield select(getChartConfig);
  let customizedOptions = merge({}, config.customizedOptions);
  let textValue = chartConfig.dataTextValue || {
    text: ''
  };

  if (!isObject(textValue)) {
    textValue = {
      text: textValue
    };
  }

  if (!textValue.text) return;
  let csv = getCSVFromText(textValue.text, textValue.amount);

  customizedOptions.data = {
    text: textValue,
    seriesMapping: customizedOptions.data.seriesMapping,
    csv
  };
  yield put(
    setProjectConfigAction({
      customizedOptions
    })
  );

  yield call(updateAggregated);
  yield call(initChart);
}

function getSeriesMapping(allOptions, dataTableFields) {
  return new Promise((resolve) => {
    let tempOption = [],
      hasExtraColumn = [],
      extraColumnBuffer = 0;

    let series = allOptions.length;

    for (let i = 0; i < series; i++) {
      let serieOption = {};
      // eslint-disable-next-line no-loop-func
      Object.keys(allOptions[i]).forEach((key) => {
        const option = allOptions[i][key];

        if (option.value !== '') {
          if (option.isData) {
            // (highed.isArray(option)) { // Data assigndata
            if (dataTableFields.indexOf(option.rawValue[0]) > -1) {
              let linkedTo = isArray(option.linkedTo) ? option.linkedTo : [option.linkedTo];
              linkedTo.forEach((linked) => {
                serieOption[linked] = dataTableFields.indexOf(option.rawValue[0]);
              });
            }
          } else {
            // eslint-disable-next-line no-undef
            if (option.linkedTo === 'label') hasLabels = true;
            if (dataTableFields.indexOf(option.rawValue[0]) > -1) {
              let linkedTo = isArray(option.linkedTo) ? option.linkedTo : [option.linkedTo];
              linkedTo.forEach((linked) => {
                serieOption[linked] = dataTableFields.indexOf(option.rawValue[0]);
              });
            }
          }

          if (option.addExtraColumn) {
            hasExtraColumn.push(option.linkedTo);
            extraColumnBuffer = option.addExtraColumn;
          }
        }
      });
      tempOption.push(serieOption);
    }

    if (hasExtraColumn.length && tempOption.length) {
      tempOption.forEach((opt) => {
        const keys = Object.keys(opt);
        keys.forEach((key) => {
          if (hasExtraColumn.indexOf(key) === -1) {
            opt[key] += extraColumnBuffer;
          }
        });
      });
    }
    return resolve(tempOption);
  });
}

export function* setData(params) {
  let { options, dataType, cb, skipNotification } = params.data;

  if (!skipNotification) {
    yield put(
      setAction({
        wizardLoadingText: 'Uploading data'
      })
    );
  }

  if (dataType === 'csv') yield call(loadCSV, params);
  else if (dataType === 'text') yield call(loadTextData);
  else {
    const isGSheet = dataType === 'google';
    const typedFunction = isGSheet ? loadGoogleSheetAction : loadLiveDataAction;

    yield put(
      typedFunction({
        options
      })
    );
  }

  yield delay(1000);

  yield put(
    setAction({
      wizardLoadingText: false
    })
  );

  // eslint-disable-next-line callback-return
  if (cb) cb();
}

/** Watch functions */
export function* watchAddBlankSeries() {
  yield takeEvery(actionTypes.chartEditor.addBlankSeries, addBlankSeries);
}
export function* watchDeleteSeries() {
  yield takeEvery(actionTypes.chartEditor.deleteSeries, deleteSeries);
}
export function* watchDeleteSingleSeries() {
  yield takeEvery(actionTypes.chartEditor.deleteSingleSeries, deleteSingleSeries);
}
export function* watchClearData() {
  yield takeEvery(actionTypes.chartEditor.clearData, clearData);
}
export function* watchLoadGoogleSheet() {
  yield takeEvery(actionTypes.chartEditor.loadGoogleSheet, loadGoogleSheet);
}
export function* watchLoadLiveData() {
  yield takeEvery(actionTypes.chartEditor.loadLiveData, loadLiveData);
}
export function* watchSetData() {
  yield takeEvery(actionTypes.chartEditor.setData, setData);
}
export function* watchLoadCSV() {
  yield takeEvery(actionTypes.chartEditor.loadCSV, loadCSV);
}
export function* watchLoadMapCSV() {
  yield takeEvery(actionTypes.chartEditor.loadMapCSV, loadMapCSV);
}

export default function* rootSaga() {
  yield all([
    watchAddBlankSeries(),
    watchDeleteSeries(),
    watchDeleteSingleSeries(),
    watchClearData(),
    watchLoadGoogleSheet(),
    watchLoadLiveData(),
    watchSetData(),
    watchLoadLiveData(),
    watchLoadMapCSV(),
    watchLoadCSV()
  ]);
}
